import React from "react";
import { Table, Button, Space, Popconfirm, Empty } from "antd";
import type {
  ColumnsType,
  TablePaginationConfig,
  TableProps,
} from "antd/es/table";
import type {
  FilterValue,
  SorterResult,
} from "antd/es/table/interface";
import { EditOutlined, DeleteOutlined, CopyOutlined } from "@ant-design/icons";
import LinkButton from "../LinkButton";

const DEFAULT_EMPTY_TEXT =
  "No hay resultados. Ajustá la búsqueda o creá un registro nuevo.";

interface DataTableProps<T> {
  data: T[];
  columns: ColumnsType<T>;
  onEdit: (record: T) => void;
  onDelete: (record: T) => void;
  /**
   * Optional: the in-app route to edit a record. When provided, the edit
   * action renders as a real anchor (so middle-click / Cmd+click / "copy link
   * address" work), while a plain left-click still navigates in-app. When
   * omitted, the action falls back to the `onEdit` callback button.
   */
  getEditHref?: (record: T) => string;
  /**
   * Optional: when provided, a "Duplicar" action renders between the edit and
   * delete buttons. Pages that don't support duplication are unaffected.
   */
  onDuplicate?: (record: T) => void;
  editButtonText?: string;
  skipDeleteConfirm?: boolean;
  loading?: boolean;
  emptyText?: string;
  rowKey?: string | ((record: T) => React.Key);
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize: number) => void;
    showSizeChanger?: boolean;
    pageSizeOptions?: string[];
  };
  /**
   * Optional: hides the edit action for rows that don't satisfy the
   * predicate (e.g. a completed record that can no longer be edited).
   * Defaults to always-editable, so existing callers are unaffected.
   */
  canEdit?: (record: T) => boolean;
  /**
   * Optional: extra per-row actions rendered between the edit and delete
   * buttons (SCRUM-1007: "Editar portada" for completed trainings). Return
   * `null` for rows that don't get the action. Omitted everywhere else, so
   * existing callers are unaffected.
   */
  extraActions?: (record: T) => React.ReactNode;
  /**
   * Optional: forwards antd's Table `onChange` (sorter/filter changes), for
   * pages that need server-driven sorting. Pagination stays owned by the
   * dedicated `pagination.onChange` above — this is only meant for sorter
   * state, so passing it does not change existing pagination behavior.
   */
  onSorterChange?: (sorter: SorterResult<T> | SorterResult<T>[]) => void;
  /**
   * Optional: forwards antd's Table `expandable`, for pages whose rows nest
   * (SCRUM-916: campuses under their main school). Omitted everywhere else,
   * so existing callers are unaffected.
   */
  expandable?: TableProps<T>["expandable"];
}

const DataTable = <T extends {}>({
  data,
  columns,
  onEdit,
  onDelete,
  getEditHref,
  onDuplicate,
  editButtonText = "Editar",
  skipDeleteConfirm = false,
  loading = false,
  emptyText = DEFAULT_EMPTY_TEXT,
  rowKey = "id",
  pagination,
  canEdit,
  extraActions,
  onSorterChange,
  expandable,
}: DataTableProps<T>) => {
  const handleTableChange = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[]
  ) => {
    onSorterChange?.(sorter);
  };

  return (
    <Table
      dataSource={data}
      rowKey={rowKey}
      loading={loading}
      locale={{ emptyText: <Empty description={emptyText} /> }}
      expandable={expandable}
      onChange={onSorterChange ? handleTableChange : undefined}
      columns={[
        ...columns,
        {
          title: "Acciones",
          key: "actions",
          width: onDuplicate || extraActions ? 300 : 180,
          render: (_, record) => (
            <Space>
              {canEdit && !canEdit(record) ? null : getEditHref ? (
                <LinkButton
                  to={getEditHref(record)}
                  type="primary"
                  icon={<EditOutlined />}
                >
                  {editButtonText}
                </LinkButton>
              ) : (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => onEdit(record)}
                >
                  {editButtonText}
                </Button>
              )}
              {extraActions?.(record)}
              {onDuplicate && (
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => onDuplicate(record)}
                >
                  Duplicar
                </Button>
              )}
              {skipDeleteConfirm ? (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => onDelete(record)}
                >
                  Eliminar
                </Button>
              ) : (
                <Popconfirm
                  title="¿Seguro que quieres eliminar este recurso?"
                  onConfirm={() => onDelete(record)}
                  okText="Sí"
                  cancelText="No"
                >
                  <Button danger icon={<DeleteOutlined />}>
                    Eliminar
                  </Button>
                </Popconfirm>
              )}
            </Space>
          ),
        },
      ]}
      pagination={
        pagination || {
          defaultPageSize: 10,
          pageSizeOptions: ["10", "20", "50", "100"],
          showSizeChanger: true,
        }
      }
    />
  );
};

export default DataTable;
